import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AgentChatStatus,
  clearAllRuns,
  fetchAgentStatus,
  fetchRuns,
  startRun as startRunOverIpc,
  subscribeToMessages,
  subscribeToRunUpdates,
  syncChatRemoteCredentials,
} from '../../services/AgentChat.service';
import { startAgentStreamingBridge } from '../../services/AgentStreamingBridge.service';
import { shipChatRun } from '../../services/ChatTelemetry.service';
import {
  AgentChatImageAttachment,
  AgentModelSelection,
  AgentRun,
} from './AgentChat.types';
import {
  ChatEngine,
  readAgentChatEngine,
  writeAgentChatEngine,
} from './agentEngine';
import { clearComposerDraft, NEW_CHAT_DRAFT_KEY } from './agentComposerDrafts';
import { isDraftTabId, nextTabsForNewChat } from './agentChatDraftTab';
import {
  readAgentChatTabState,
  reconcileAgentChatTabState,
  writeAgentChatTabState,
} from './agentChatTabPersistence';
import { createAgentStreamBatcher } from './agentStreamBatch';
import { DEFAULT_AGENT_DEV_MODEL, toModelSelection } from './agentDevModels';

/**
 * The conversations live above the router so leaving the homepage does not end
 * them. Runs come back from storage on their own, but which ones are open is
 * only held here — mounting this per page meant every trip to another page
 * dropped the DJ back to the history list. It resets on app close.
 *
 * Several conversations can be open at once and each keeps running while the
 * others are on screen; the main process has always been able to run them in
 * parallel, keyed by run id.
 *
 * Which tabs are open is remembered in localStorage so a page refresh puts the
 * rail back the way the DJ left it; conversations themselves still come from
 * the agent_runs store in main.
 */

type AgentChatSessionValue = {
  runs: AgentRun[];
  /** Conversations with a tab, in the order they were opened. */
  openTabs: AgentRun[];
  activeRun: AgentRun | null;
  /** null is the blank slate, where the next question starts a conversation. */
  activeTabId: string | null;
  configured: boolean | null;
  cursorConfigured: boolean | null;
  directConfigured: boolean | null;
  viewingHistory: boolean;
  toggleHistory: () => void;
  openRun: (runId: string) => void;
  closeTab: (runId: string) => void;
  newChat: () => void;
  clearHistory: () => Promise<void>;
  send: (
    prompt: string,
    conversationId?: string,
    images?: AgentChatImageAttachment[],
  ) => void;
  selectedModel: AgentModelSelection;
  setSelectedModel: (selection: AgentModelSelection) => void;
  selectedEngine: ChatEngine;
  setSelectedEngine: (engine: ChatEngine) => void;
};

const AgentChatSessionContext = createContext<AgentChatSessionValue | null>(
  null,
);

export const useAgentChatSession = (): AgentChatSessionValue => {
  const session = useContext(AgentChatSessionContext);
  if (!session) {
    throw new Error(
      'Agent chat needs an AgentChatSessionProvider above it in the tree.',
    );
  }
  return session;
};

export const AgentChatSessionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [openTabIds, setOpenTabIds] = useState<string[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [status, setStatus] = useState<AgentChatStatus | null>(null);
  const [viewingHistory, setViewingHistory] = useState(false);
  const [selectedEngine, setSelectedEngineState] =
    useState<ChatEngine>(readAgentChatEngine);
  const [selectedModel, setSelectedModel] = useState<AgentModelSelection>(() =>
    toModelSelection(DEFAULT_AGENT_DEV_MODEL),
  );
  const setRunsRef = useRef(setRuns);
  setRunsRef.current = setRuns;
  const tabsHydratedRef = useRef(false);

  useEffect(() => {
    if (!tabsHydratedRef.current) return;
    writeAgentChatTabState({
      openTabIds: openTabIds.filter((id) => !isDraftTabId(id)),
      activeTabId:
        activeTabId && !isDraftTabId(activeTabId) ? activeTabId : null,
    });
  }, [openTabIds, activeTabId]);

  useEffect(() => {
    let cancelled = false;
    const streamBatch = createAgentStreamBatcher((updater) => {
      setRunsRef.current(updater);
    });

    void (async () => {
      syncChatRemoteCredentials();
      const [available, history] = await Promise.all([
        fetchAgentStatus(),
        fetchRuns(),
      ]);
      if (cancelled) return;
      setStatus(available);
      setRuns(history);

      const runIds = new Set(history.map((run) => run.id));
      const stored = readAgentChatTabState();
      let openTabIds: string[] = [];
      let activeTabId: string | null = null;

      if (stored) {
        const reconciled = reconcileAgentChatTabState(stored, runIds);
        openTabIds = reconciled.openTabIds;
        activeTabId = reconciled.activeTabId;
      }

      // Anything still running after a refresh should stay visible even if
      // persistence was empty on an older build.
      for (const run of history) {
        if (
          (run.status === 'running' || run.status === 'queued') &&
          !openTabIds.includes(run.id)
        ) {
          openTabIds.push(run.id);
        }
      }

      if (openTabIds.length > 0) {
        setOpenTabIds(openTabIds);
        setActiveTabId(activeTabId);
      }

      tabsHydratedRef.current = true;
    })();

    const unsubscribeMessages = subscribeToMessages((runId, message) => {
      streamBatch.queue(runId, message);
    });

    const unsubscribeRuns = subscribeToRunUpdates((updated) => {
      streamBatch.flushNow();
      // Settled conversations go to the chat-logs dashboard; no-op when the
      // telemetry keys are absent or the run is still streaming.
      shipChatRun(updated);
      setRuns((current) => {
        const index = current.findIndex((item) => item.id === updated.id);
        if (index === -1) return [updated, ...current];
        const next = [...current];
        // A finished run arrives with the full transcript, so trust the payload
        // when it has more lines than the messages streamed so far.
        next[index] =
          updated.messages.length >= next[index].messages.length
            ? updated
            : { ...updated, messages: next[index].messages };
        return next;
      });
    });

    return () => {
      cancelled = true;
      streamBatch.dispose();
      unsubscribeMessages();
      unsubscribeRuns();
    };
  }, []);

  useEffect(() => startAgentStreamingBridge(), []);

  const resolveOpenTab = useCallback(
    (id: string, history: AgentRun[]): AgentRun | null => {
      const run = history.find((item) => item.id === id);
      if (run) return run;
      if (isDraftTabId(id)) {
        return {
          id,
          prompt: 'New chat',
          status: 'completed',
          createdAt: new Date().toISOString(),
          messages: [],
          engine: selectedEngine,
        };
      }
      return null;
    },
    [selectedEngine],
  );

  const activeRun = useMemo(
    () => (activeTabId ? resolveOpenTab(activeTabId, runs) : null),
    [activeTabId, resolveOpenTab, runs],
  );

  const openTabs = useMemo(
    () =>
      openTabIds
        .map((id) => resolveOpenTab(id, runs))
        .filter((run): run is AgentRun => Boolean(run)),
    [openTabIds, resolveOpenTab, runs],
  );

  const configured = status === null ? null : status.directConfigured;

  /** Gives a conversation a tab if it has none, and brings it to the front. */
  const openRun = useCallback((runId: string) => {
    setOpenTabIds((current) =>
      current.includes(runId) ? current : [...current, runId],
    );
    setActiveTabId(runId);
    setViewingHistory(false);
  }, []);

  /**
   * Sends a question. With a conversation open it becomes another turn in that
   * conversation, so the agent keeps its context; otherwise it starts a new one.
   */
  const send = useCallback(
    (
      prompt: string,
      conversationId?: string,
      images?: AgentChatImageAttachment[],
    ) => {
      const model = selectedModel;
      const fromDraft = Boolean(conversationId && isDraftTabId(conversationId));
      const runId = fromDraft
        ? crypto.randomUUID()
        : (conversationId ?? crypto.randomUUID());
      const now = new Date().toISOString();
      const question = {
        id: crypto.randomUUID(),
        role: 'user' as const,
        content: prompt,
        createdAt: now,
        ...(images?.length ? { attachments: images } : {}),
      };

      if (fromDraft && conversationId) {
        setOpenTabIds((current) =>
          current.map((id) => (id === conversationId ? runId : id)),
        );
        setActiveTabId(runId);
        clearComposerDraft(conversationId);
      }

      // Show the question immediately; main replaces this with the stored run.
      setRuns((current) => {
        const index = current.findIndex((item) => item.id === runId);
        if (index === -1) {
          return [
            {
              id: runId,
              prompt,
              status: 'queued',
              createdAt: now,
              messages: [question],
              engine: selectedEngine,
            },
            ...current,
          ];
        }
        const next = [...current];
        next[index] = {
          ...next[index],
          status: 'queued',
          messages: [...next[index].messages, question],
        };
        return next;
      });

      openRun(runId);
      startRunOverIpc(
        runId,
        prompt,
        model,
        selectedEngine,
        images,
        question.id,
      );
    },
    [openRun, selectedEngine, selectedModel],
  );

  /**
   * Closing a tab leaves the conversation in history and lets any run it has in
   * flight finish. Unlike the canvas tabs, this steps to a neighbour rather than
   * the blank slate — dropping the DJ on an empty composer would hide the other
   * conversations they still have working.
   */
  const closeTab = useCallback(
    (runId: string) => {
      const index = openTabIds.indexOf(runId);
      if (index === -1) return;
      clearComposerDraft(runId);
      const remaining = openTabIds.filter((id) => id !== runId);
      setOpenTabIds(remaining);
      if (activeTabId === runId) {
        setActiveTabId(remaining[index - 1] ?? remaining[index] ?? null);
      }
    },
    [activeTabId, openTabIds],
  );

  const newChat = useCallback(() => {
    setViewingHistory(false);
    setOpenTabIds((current) => {
      const next = nextTabsForNewChat(current);
      setActiveTabId(next.activeTabId);
      return next.openTabIds;
    });
  }, []);

  const setSelectedEngine = useCallback(
    (engine: ChatEngine) => {
      if (engine === selectedEngine) return;
      writeAgentChatEngine(engine);
      setSelectedEngineState(engine);
      newChat();
    },
    [newChat, selectedEngine],
  );

  const clearHistory = useCallback(async () => {
    for (const tabId of openTabIds) {
      clearComposerDraft(tabId);
    }
    clearComposerDraft(NEW_CHAT_DRAFT_KEY);

    const history = await clearAllRuns();
    setRuns(history);
    setOpenTabIds([]);
    setActiveTabId(null);
    setViewingHistory(false);
    writeAgentChatTabState({ openTabIds: [], activeTabId: null });
  }, [openTabIds]);

  const toggleHistory = useCallback(
    () => setViewingHistory((open) => !open),
    [],
  );

  const value = useMemo(
    () => ({
      runs,
      openTabs,
      activeRun,
      activeTabId,
      configured,
      cursorConfigured: status?.cursorConfigured ?? null,
      directConfigured: status?.directConfigured ?? null,
      viewingHistory,
      toggleHistory,
      openRun,
      closeTab,
      newChat,
      clearHistory,
      send,
      selectedModel,
      setSelectedModel,
      selectedEngine,
      setSelectedEngine,
    }),
    [
      runs,
      openTabs,
      activeRun,
      activeTabId,
      configured,
      status,
      viewingHistory,
      toggleHistory,
      openRun,
      closeTab,
      newChat,
      clearHistory,
      send,
      selectedModel,
      selectedEngine,
      setSelectedEngine,
    ],
  );

  return (
    <AgentChatSessionContext.Provider value={value}>
      {children}
    </AgentChatSessionContext.Provider>
  );
};
